import { useEffect, useState, useRef } from "react";
import { applyLeave, getLeaveBalance, getMyLeaves } from "@/api/leave";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { format, addDays } from "date-fns";
import { parseISO } from "date-fns/parseISO";

// Helper to get all dates between two dates (inclusive)
function getDatesInRange(start: string, end: string): string[] {
  const dates = [];
  let current = new Date(start);
  const last = new Date(end);
  while (current <= last) {
    dates.push(current.toISOString().slice(0, 10));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

// Helper: parse ISO-like datetime to local display string
const parseToLocal = (iso?: string) => {
  if (!iso) return "";
  // if string already contains timezone info (Z or +HH:MM/-HH:MM), use as-is
  const hasTZ = /Z|[+\-]\d{2}:\d{2}$/.test(iso);
  const ds = hasTZ ? iso : `${iso}Z`; // treat no-tz strings as UTC
  const d = new Date(ds);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const LeaveManagement = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [leaves, setLeaves] = useState<any[]>([]);
  const [form, setForm] = useState({ from: "", to: "", remarks: "" });
  const [isApplying, setIsApplying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<"success" | "error">("success");
  const messageTimeout = useRef<NodeJS.Timeout | null>(null);
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  useEffect(() => {
    fetchBalance();
    fetchLeaves();
    // Cleanup message timeout on unmount
    return () => {
      if (messageTimeout.current) clearTimeout(messageTimeout.current);
    };
  }, []);

  const fetchBalance = async () => {
    setIsLoading(true);
    try {
      const data = await getLeaveBalance();
      setBalance(data.available_leave);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLeaves = async () => {
    setIsLoading(true);
    try {
      const data = await getMyLeaves();
      setLeaves(data);
    } finally {
      setIsLoading(false);
    }
  };

  const getNumDays = () => {
    if (!form.from || !form.to) return 0;
    const fromDate = new Date(form.from);
    const toDate = new Date(form.to);
    if (toDate < fromDate) return 0;
    return Math.floor((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleApply = async () => {
    setIsApplying(true);
    setMessage(null);
    setMessageType("success");
    if (messageTimeout.current) clearTimeout(messageTimeout.current);

    try {
      const numDays = getNumDays();
      if (!form.from || !form.to || numDays <= 0) {
        setMessage("Please select a valid date range.");
        setMessageType("error");
        setIsApplying(false);
        messageTimeout.current = setTimeout(() => setMessage(null), 10000);
        return;
      }
      const dates = getDatesInRange(form.from, form.to);
      let errors: { date: string; detail: string }[] = [];

      for (const date of dates) {
        try {
          // Call API and inspect response body for success flag
          const resp = await applyLeave({ leave_date: date, duration: 1, remarks: form.remarks });

          // If backend returns an object with success===false, treat as error
          if (resp && typeof resp === "object" && "success" in resp && resp.success === false) {
            const detail = resp.detail || resp.message || "Failed to apply leave";
            errors.push({ date, detail });
          }
          // Otherwise assume success (existing behavior)
        } catch (err: any) {
          let detail = "Failed to apply leave";
          if (err?.response?.data?.detail) {
            detail = err.response.data.detail;
          } else if (err?.response?.data?.message) {
            detail = err.response.data.message;
          } else if (err?.message) {
            detail = err.message;
          }
          errors.push({ date, detail });
        }
      }

      if (errors.length === 0) {
        setMessage(`Leave applied successfully for ${numDays} day(s).`);
        setMessageType("success");
      } else {
        // Show backend detail(s)
        setMessage(
          errors
            .map((e) => `${e.date}: ${e.detail}`)
            .join("; ")
        );
        setMessageType("error");
      }

      setForm({ from: "", to: "", remarks: "" });
      fetchBalance();
      fetchLeaves();
    } catch (err: any) {
      setMessage("Failed to apply for leave.");
      setMessageType("error");
    } finally {
      setIsApplying(false);
      messageTimeout.current = setTimeout(() => setMessage(null), 10000);
    }
  };

  // Calculate used leave for the chart
  const usedLeave = leaves.reduce((sum, leave) => sum + (leave.duration || 0), 0);
  const available = balance ?? 0;
  const total = usedLeave + available;
  const percentUsed = total > 0 ? (usedLeave / total) * 100 : 0;

  return (
    <div className="space-y-8 relative">
      {/* Processing overlay */}
      {(isApplying || isLoading) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70 dark:bg-black/70">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-10 w-10 text-blue-600 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <span className="text-blue-700 dark:text-blue-300 font-medium">Processing...</span>
          </div>
        </div>
      )}

      <h1 className="text-2xl font-bold text-foreground">Leave Management</h1>
      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex-1 bg-card text-card-foreground rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Apply for Leave</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground">From</label>
              <Popover open={fromOpen} onOpenChange={setFromOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="mt-1 block w-full border border-input rounded px-3 py-2 text-left bg-background text-foreground hover:border-ring/50"
                    onClick={() => setFromOpen(true)}
                  >
                    {form.from ? format(parseISO(form.from), "yyyy-MM-dd") : "Select start date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto">
                  <Calendar
                    mode="single"
                    selected={form.from ? parseISO(form.from) : undefined}
                    onSelect={date => {
                      setForm(f => ({ ...f, from: date ? format(date, "yyyy-MM-dd") : "" }));
                      setFromOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">To</label>
              <Popover open={toOpen} onOpenChange={setToOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="mt-1 block w-full border border-input rounded px-3 py-2 text-left bg-background text-foreground hover:border-ring/50"
                    onClick={() => setToOpen(true)}
                  >
                    {form.to ? format(parseISO(form.to), "yyyy-MM-dd") : "Select end date"}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="p-0 w-auto">
                  <Calendar
                    mode="single"
                    selected={form.to ? parseISO(form.to) : undefined}
                    onSelect={date => {
                      setForm(f => ({ ...f, to: date ? format(date, "yyyy-MM-dd") : "" }));
                      setToOpen(false);
                    }}
                    initialFocus
                    fromDate={form.from ? parseISO(form.from) : undefined}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Number of Days</label>
              <input
                type="number"
                className="mt-1 block w-full border border-input rounded px-3 py-2 bg-muted text-muted-foreground"
                value={getNumDays()}
                readOnly
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Remarks</label>
              <input
                type="text"
                className="mt-1 block w-full border border-input rounded px-3 py-2 bg-background text-foreground"
                value={form.remarks}
                onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
              />
            </div>
            <button
              className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
              onClick={handleApply}
              disabled={isApplying}
            >
              {isApplying ? "Applying..." : "Apply Leave"}
            </button>
            {message && (
              <div
                className={`mt-2 text-sm rounded px-3 py-2 ${
                  messageType === "success"
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                    : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                }`}
              >
                {message}
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 bg-card text-card-foreground rounded-lg shadow p-6 flex flex-col items-center justify-center">
          <h2 className="text-lg font-semibold mb-4">Leave Balance</h2>
          <div className="flex flex-col items-center justify-center w-full h-full">
            {/* Circular Progress Chart */}
            <div className="relative flex items-center justify-center mb-4" style={{ width: 160, height: 160 }}>
              <svg width="160" height="160">
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="#e5e7eb"
                  strokeWidth="16"
                  fill="none"
                />
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="#243c5a"
                  strokeWidth="16"
                  fill="none"
                  strokeDasharray={2 * Math.PI * 70}
                  strokeDashoffset={2 * Math.PI * 70 * (1 - percentUsed / 100)}
                  strokeLinecap="round"
                  style={{ transition: "stroke-dashoffset 0.5s" }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-300">{available}</span>
                <span className="text-sm text-muted-foreground">Available</span>
              </div>
            </div>
            <div className="flex justify-between w-full mt-2">
              <div className="flex flex-col items-center flex-1">
                <span className="text-lg font-semibold text-blue-700 dark:text-blue-300">{leaves.length}</span>
                <span className="text-xs text-muted-foreground"># of Applied Leave(s)</span>
              </div>
              <div className="flex flex-col items-center flex-1">
                <span className="text-lg font-semibold text-green-700 dark:text-green-300">{available}</span>
                <span className="text-xs text-muted-foreground">Available</span>
              </div>
              <div className="flex flex-col items-center flex-1">
                <span className="text-lg font-semibold text-foreground">{total}</span>
                <span className="text-xs text-muted-foreground">Total</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card text-card-foreground rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Leave History</h2>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead># of Days</TableHead>
              <TableHead>Applied At</TableHead>
              <TableHead>Remarks</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {leaves.map((leave) => (
              <TableRow key={leave.leave_id}>
                <TableCell>{leave.leave_date}</TableCell>
                <TableCell>{leave.duration}</TableCell>
                <TableCell>{parseToLocal(leave.applied_at)}</TableCell>
                <TableCell>{leave.remarks}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default LeaveManagement;
